import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Slider } from '../components/ui/slider';
import { recalculateAllGradesAndSummaries } from '../../utils/supabase/analytics';
import {
  ArrowLeft,
  Save,
  CheckSquare,
  MessageSquare,
  Target,
  PenTool,
  Gamepad2,
  Cloud,
  Info,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

interface ActivityWeight {
  id: string;
  name: string;
  icon: any;
  weight: number;
  enabled: boolean;
}

export default function GradeSettings() {
  const navigate = useNavigate();
  const [recalculating, setRecalculating] = useState(false);

  const [activityWeights, setActivityWeights] = useState<ActivityWeight[]>([
    { id: 'quiz', name: 'Multiple Choice Quizzes', icon: CheckSquare, weight: 30, enabled: true },
    { id: 'open', name: 'Open-Ended Questions', icon: MessageSquare, weight: 25, enabled: true },
    { id: 'matching', name: 'Matching Activities', icon: Target, weight: 15, enabled: true },
    { id: 'draw', name: 'Draw-It Activities', icon: PenTool, weight: 15, enabled: true },
    { id: 'games', name: 'Interactive Games', icon: Gamepad2, weight: 10, enabled: true },
    { id: 'polls', name: 'Polls & Word Clouds', icon: Cloud, weight: 5, enabled: false },
  ]);

  const [settings, setSettings] = useState({
    participationGrading: true,
    participationWeight: 10,
    autoGradeEnabled: true,
    manualOverrideEnabled: true,
    lateSubmissionPenalty: true,
    lateSubmissionPercentage: 10,
    roundScores: true,
    showStudentScores: true,
    allowScoreRevisions: true,
  });

  const handleWeightChange = (id: string, value: number) => {
    setActivityWeights(
      activityWeights.map((a) => (a.id === id ? { ...a, weight: value } : a))
    );
  };

  const handleToggleActivity = (id: string) => {
    setActivityWeights(
      activityWeights.map((a) => (a.id === id ? { ...a, enabled: !a.enabled } : a))
    );
  };

  const handleSave = () => {
    toast.success('Grade settings saved successfully!');
    navigate('/gradebook');
  };

  const handleRecalculateGrades = async () => {
    setRecalculating(true);
    try {
      const affectedRecords = await recalculateAllGradesAndSummaries();
      toast.success(`Successfully recalculated ${affectedRecords} grade records`);
    } catch (error) {
      console.error('Error recalculating grades:', error);
      toast.error('Failed to recalculate grades. Please try again.');
    } finally {
      setRecalculating(false);
    }
  };

  const totalWeight = activityWeights
    .filter((a) => a.enabled)
    .reduce((sum, a) => sum + a.weight, 0);

  return (
    <>
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/gradebook')}
                className="rounded-xl"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Gradebook
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Grade Settings</h1>
                <p className="text-sm text-muted-foreground">
                  Configure grading weights and preferences
                </p>
              </div>
            </div>
            <Button onClick={handleSave} className="rounded-xl">
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Activity Weights */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Activity Weights</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Adjust how much each activity type contributes to the final grade
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Total Weight</p>
              <p
                className={`text-2xl font-bold ${
                  totalWeight === 100 ? 'text-green-600' : 'text-orange-600'
                }`}
              >
                {totalWeight}%
              </p>
            </div>
          </div>

          {totalWeight !== 100 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6 flex items-start gap-3">
              <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-orange-900">
                  Weight total should equal 100%
                </p>
                <p className="text-sm text-orange-800 mt-1">
                  Currently {totalWeight > 100 ? 'over' : 'under'} by{' '}
                  {Math.abs(100 - totalWeight)}%
                </p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {activityWeights.map((activity) => (
              <div key={activity.id}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={activity.enabled}
                      onCheckedChange={() => handleToggleActivity(activity.id)}
                    />
                    <div className="flex items-center gap-2">
                      <activity.icon className="w-5 h-5 text-primary" />
                      <Label className="cursor-pointer">{activity.name}</Label>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      value={activity.weight}
                      onChange={(e) =>
                        handleWeightChange(activity.id, Number(e.target.value))
                      }
                      disabled={!activity.enabled}
                      className="w-20 rounded-lg text-center"
                      min="0"
                      max="100"
                    />
                    <span className="text-sm font-medium text-muted-foreground w-8">%</span>
                  </div>
                </div>
                <Slider
                  value={[activity.weight]}
                  onValueChange={(value) => handleWeightChange(activity.id, value[0])}
                  max={100}
                  step={5}
                  disabled={!activity.enabled}
                  className="w-full"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Participation Grading */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Participation Grading
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Enable Participation Grading</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Include participation as part of the final grade
                </p>
              </div>
              <Switch
                checked={settings.participationGrading}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, participationGrading: checked })
                }
              />
            </div>

            {settings.participationGrading && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Participation Weight</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={settings.participationWeight}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            participationWeight: Number(e.target.value),
                          })
                        }
                        className="w-20 rounded-lg text-center"
                        min="0"
                        max="50"
                      />
                      <span className="text-sm font-medium text-muted-foreground w-8">%</span>
                    </div>
                  </div>
                  <Slider
                    value={[settings.participationWeight]}
                    onValueChange={(value) =>
                      setSettings({ ...settings, participationWeight: value[0] })
                    }
                    max={50}
                    step={5}
                    className="w-full"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Grading Options */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Grading Options</h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <Label>Auto-Grade Enabled Activities</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Automatically grade quizzes, matching, and games
                </p>
              </div>
              <Switch
                checked={settings.autoGradeEnabled}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, autoGradeEnabled: checked })
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between py-3">
              <div>
                <Label>Allow Manual Override</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Teachers can manually adjust auto-graded scores
                </p>
              </div>
              <Switch
                checked={settings.manualOverrideEnabled}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, manualOverrideEnabled: checked })
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between py-3">
              <div>
                <Label>Round Final Scores</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Round final scores to nearest whole number
                </p>
              </div>
              <Switch
                checked={settings.roundScores}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, roundScores: checked })
                }
              />
            </div>
          </div>
        </div>

        {/* Late Submission Policy */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Late Submission Policy
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Apply Late Submission Penalty</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Reduce score for late submissions
                </p>
              </div>
              <Switch
                checked={settings.lateSubmissionPenalty}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, lateSubmissionPenalty: checked })
                }
              />
            </div>

            {settings.lateSubmissionPenalty && (
              <>
                <Separator />
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label>Penalty Percentage</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={settings.lateSubmissionPercentage}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            lateSubmissionPercentage: Number(e.target.value),
                          })
                        }
                        className="w-20 rounded-lg text-center"
                        min="0"
                        max="100"
                      />
                      <span className="text-sm font-medium text-muted-foreground w-8">%</span>
                    </div>
                  </div>
                  <Slider
                    value={[settings.lateSubmissionPercentage]}
                    onValueChange={(value) =>
                      setSettings({ ...settings, lateSubmissionPercentage: value[0] })
                    }
                    max={100}
                    step={5}
                    className="w-full"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Data Maintenance */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6 mt-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Data Maintenance</h2>
          <p className="text-sm text-muted-foreground mb-4">
            If grades are not displaying correctly in reports and analytics, you can trigger a recalculation to sync all grades from your submissions.
          </p>
          <Button 
            onClick={handleRecalculateGrades}
            disabled={recalculating}
            variant="outline"
            className="rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
            {recalculating ? 'Recalculating...' : 'Recalculate All Grades'}
          </Button>
        </div>

        {/* Student Visibility */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Student Visibility
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between py-3">
              <div>
                <Label>Show Scores to Students</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Students can view their scores and feedback
                </p>
              </div>
              <Switch
                checked={settings.showStudentScores}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, showStudentScores: checked })
                }
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between py-3">
              <div>
                <Label>Allow Score Revision Requests</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Students can request grade reviews
                </p>
              </div>
              <Switch
                checked={settings.allowScoreRevisions}
                onCheckedChange={(checked) =>
                  setSettings({ ...settings, allowScoreRevisions: checked })
                }
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/gradebook')} className="rounded-xl">
            Cancel
          </Button>
          <Button onClick={handleSave} className="rounded-xl">
            <Save className="w-4 h-4 mr-2" />
            Save All Settings
          </Button>
        </div>
      </div>
    </>
  );
}